/**
 * Curriculum Download Utility
 * 
 * Handles client-side generation of offline packages for curriculum content.
 * Supports both development mode (unsigned ZIP) and production mode (signed .dnshch).
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.5
 */

import JSZip from 'jszip';

// Package manifest entry
export interface ContentManifestEntry {
  id: string;
  grade: number;
  course: string;
  topic: number;
  type: string;
  title: string;
  titleFa: string;
  filename: string;
  duration?: number;
  size: number;
}

// Package manifest
export interface PackageManifest {
  version: string;
  school: string;
  generatedAt: string;
  format: 'development' | 'production';
  deviceId?: string;
  content: ContentManifestEntry[];
}

// Signed manifest structure
export interface SignedManifest {
  manifestJson: string;
  signature: string;
}

// File entry from API
export interface FileEntry {
  id: string;
  filename: string;
  r2Path: string;
  size: number;
  exists: boolean;
}

// Download progress callback
export interface DownloadProgress {
  phase: 'preparing' | 'downloading' | 'packaging';
  currentFile: string;
  currentIndex: number;
  totalFiles: number;
  percentComplete: number;
  bytesDownloaded: number;
  totalBytes: number;
}

export type ProgressCallback = (progress: DownloadProgress) => void;

// API response for download request
export interface DownloadApiResponse {
  data?: {
    manifest: PackageManifest;
    signedManifest?: SignedManifest;
    files: FileEntry[];
    totalSize: number;
    format: 'development' | 'production';
    deviceId?: string;
    message: string;
    message_fa: string;
  };
  error?: {
    code: string;
    message: string;
    message_fa: string;
  };
}

/**
 * Request download package from API
 */
export async function requestDownloadPackage(
  contentIds: string[],
  format: 'development' | 'production',
  accessToken: string,
  deviceId?: string
): Promise<DownloadApiResponse> {
  const response = await fetch('/api/student/curriculum/download', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      contentIds,
      format,
      deviceId
    })
  });

  return await response.json();
}

/**
 * Download a single file from R2 via the API
 */
async function downloadFile(
  r2Path: string,
  accessToken: string
): Promise<ArrayBuffer> {
  const response = await fetch(`/api/student/curriculum/download?path=${encodeURIComponent(r2Path)}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to download file: ${r2Path}`);
  }

  return await response.arrayBuffer();
}

/**
 * Generate development mode ZIP package
 * 
 * Creates a ZIP file containing:
 * - manifest.json with content metadata
 * - Media files organized by topic
 * 
 * Requirements: 9.1, 9.2
 */
export async function generateDevelopmentPackage(
  manifest: PackageManifest,
  files: FileEntry[],
  accessToken: string,
  onProgress?: ProgressCallback
): Promise<Blob> {
  const zip = new JSZip();
  
  // Calculate total bytes
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  let bytesDownloaded = 0;

  // Report initial progress
  onProgress?.({
    phase: 'preparing',
    currentFile: '',
    currentIndex: 0,
    totalFiles: files.length,
    percentComplete: 0,
    bytesDownloaded: 0,
    totalBytes
  });

  // Add manifest.json
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  // Download and add each file
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const contentEntry = manifest.content.find(c => c.id === file.id);
    
    if (!contentEntry) continue;

    // Report progress
    onProgress?.({
      phase: 'downloading',
      currentFile: file.filename,
      currentIndex: i + 1,
      totalFiles: files.length,
      percentComplete: Math.round((i / files.length) * 100),
      bytesDownloaded,
      totalBytes
    });

    try {
      // Download file
      const fileData = await downloadFile(file.r2Path, accessToken);
      
      // Organize by topic: topic-{n}/{filename}
      const topicFolder = `topic-${contentEntry.topic}`;
      zip.file(`${topicFolder}/${file.filename}`, fileData);
      
      bytesDownloaded += file.size;
    } catch (error) {
      console.error(`Failed to download ${file.filename}:`, error);
      // Continue with other files
    }
  }

  // Report packaging progress
  onProgress?.({
    phase: 'packaging',
    currentFile: 'Creating ZIP...',
    currentIndex: files.length,
    totalFiles: files.length,
    percentComplete: 95,
    bytesDownloaded,
    totalBytes
  });

  // Generate ZIP blob
  const blob = await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });

  // Report completion
  onProgress?.({
    phase: 'packaging',
    currentFile: 'Complete',
    currentIndex: files.length,
    totalFiles: files.length,
    percentComplete: 100,
    bytesDownloaded: totalBytes,
    totalBytes
  });

  return blob;
}

/**
 * Trigger browser download of a blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generate package filename from manifest
 */
export function generatePackageFilename(manifest: PackageManifest): string {
  const date = new Date().toISOString().split('T')[0];
  const school = manifest.school.replace(/[^a-zA-Z0-9]/g, '-').substring(0, 30);
  const ext = manifest.format === 'production' ? 'dnshch.signed' : 'zip';
  return `${school}-${date}.${ext}`;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Estimate download time based on file size
 * Assumes average connection speed of 5 Mbps
 */
export function estimateDownloadTime(bytes: number): string {
  const speedBps = 5 * 1024 * 1024 / 8; // 5 Mbps in bytes per second
  const seconds = Math.ceil(bytes / speedBps);
  
  if (seconds < 60) return `~${seconds} seconds`;
  if (seconds < 3600) return `~${Math.ceil(seconds / 60)} minutes`;
  return `~${(seconds / 3600).toFixed(1)} hours`;
}


// Signature header constants (must match SecurityModule and API)
const SIGNATURE_MAGIC = 'DNSHCH-SIGNED-V1';
const SIGNATURE_HEADER_SIZE = 256;

/**
 * Generate production mode signed package (.dnshch.signed)
 * 
 * Creates a signed package containing:
 * - 256-byte signature header
 * - ZIP content with manifest.signed and media files
 * 
 * The signature header allows device firmware to verify
 * the package is authorized for the specific device.
 * 
 * Requirements: 9.5
 */
export async function generateProductionPackage(
  manifest: PackageManifest,
  signedManifest: SignedManifest,
  files: FileEntry[],
  accessToken: string,
  deviceId: string,
  onProgress?: ProgressCallback
): Promise<Blob> {
  const zip = new JSZip();
  
  // Calculate total bytes
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  let bytesDownloaded = 0;

  // Report initial progress
  onProgress?.({
    phase: 'preparing',
    currentFile: '',
    currentIndex: 0,
    totalFiles: files.length,
    percentComplete: 0,
    bytesDownloaded: 0,
    totalBytes
  });

  // Add signed manifest
  zip.file('manifest.signed', JSON.stringify(signedManifest, null, 2));
  
  // Also add regular manifest for reference
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  // Download and add each file
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const contentEntry = manifest.content.find(c => c.id === file.id);
    
    if (!contentEntry) continue;

    // Report progress
    onProgress?.({
      phase: 'downloading',
      currentFile: file.filename,
      currentIndex: i + 1,
      totalFiles: files.length,
      percentComplete: Math.round((i / files.length) * 100),
      bytesDownloaded,
      totalBytes
    });

    try {
      // Download file
      const fileData = await downloadFile(file.r2Path, accessToken);
      
      // Organize by topic: topic-{n}/{filename}
      const topicFolder = `topic-${contentEntry.topic}`;
      zip.file(`${topicFolder}/${file.filename}`, fileData);
      
      bytesDownloaded += file.size;
    } catch (error) {
      console.error(`Failed to download ${file.filename}:`, error);
      // Continue with other files
    }
  }

  // Report packaging progress
  onProgress?.({
    phase: 'packaging',
    currentFile: 'Creating signed package...',
    currentIndex: files.length,
    totalFiles: files.length,
    percentComplete: 90,
    bytesDownloaded,
    totalBytes
  });

  // Generate ZIP blob
  const zipBlob = await zip.generateAsync({ 
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });

  // Create signature header
  const header = createSignatureHeader(deviceId, signedManifest.signature);

  // Combine header + ZIP
  const signedPackage = new Uint8Array(header.length + zipBlob.byteLength);
  signedPackage.set(header, 0);
  signedPackage.set(new Uint8Array(zipBlob), header.length);

  // Report completion
  onProgress?.({
    phase: 'packaging',
    currentFile: 'Complete',
    currentIndex: files.length,
    totalFiles: files.length,
    percentComplete: 100,
    bytesDownloaded: totalBytes,
    totalBytes
  });

  return new Blob([signedPackage], { type: 'application/octet-stream' });
}

/**
 * Create signature header for signed package
 * 
 * Header structure (256 bytes):
 * - Bytes 0-15: Magic string "DNSHCH-SIGNED-V1"
 * - Bytes 16-37: Device ID (22 chars)
 * - Bytes 38-45: Timestamp (8 bytes, big-endian uint64)
 * - Bytes 46-77: HMAC signature (32 bytes)
 * - Bytes 78-255: Reserved (zero-filled)
 */
function createSignatureHeader(deviceId: string, signatureHex: string): Uint8Array {
  const header = new Uint8Array(SIGNATURE_HEADER_SIZE);
  
  // Magic string (16 bytes)
  const magicBytes = new TextEncoder().encode(SIGNATURE_MAGIC);
  header.set(magicBytes, 0);
  
  // Device ID (22 bytes)
  const deviceIdBytes = new TextEncoder().encode(deviceId);
  header.set(deviceIdBytes, 16);
  
  // Timestamp (8 bytes, big-endian)
  const timestamp = BigInt(Date.now());
  const timestampView = new DataView(header.buffer, 38, 8);
  timestampView.setBigUint64(0, timestamp, false);
  
  // HMAC signature (32 bytes) - convert from hex
  const signatureBytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    signatureBytes[i] = parseInt(signatureHex.substr(i * 2, 2), 16);
  }
  header.set(signatureBytes, 46);
  
  return header;
}

/**
 * Validate device ID format
 * Expected format: DNSHCH-{12 hex chars}-{2 hex chars}
 */
export function validateDeviceId(deviceId: string): boolean {
  if (!deviceId || typeof deviceId !== 'string') {
    return false;
  }
  const pattern = /^DNSHCH-[0-9A-F]{12}-[0-9A-F]{2}$/i;
  if (!pattern.test(deviceId)) {
    return false;
  }
  // Extract MAC and checksum
  const mac = deviceId.substring(7, 19);
  const checksum = deviceId.substring(20, 22);
  const calculatedChecksum = calculateCRC8(mac);
  return calculatedChecksum.toUpperCase() === checksum.toUpperCase();
}

/**
 * Calculate CRC8 checksum for MAC address
 */
function calculateCRC8(macHex: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < macHex.length; i += 2) {
    bytes.push(parseInt(macHex.substr(i, 2), 16));
  }
  let crc = 0x00;
  for (const byte of bytes) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) {
      if (crc & 0x80) {
        crc = (crc << 1) ^ 0x07;
      } else {
        crc = crc << 1;
      }
      crc &= 0xFF;
    }
  }
  return crc.toString(16).padStart(2, '0').toUpperCase();
}

/**
 * High-level function to download curriculum content
 * Handles both development and production modes
 */
export async function downloadCurriculumContent(
  contentIds: string[],
  format: 'development' | 'production',
  accessToken: string,
  deviceId?: string,
  onProgress?: ProgressCallback
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate device ID for production mode
    if (format === 'production') {
      if (!deviceId) {
        return { success: false, error: 'Device ID is required for production mode' };
      }
      if (!validateDeviceId(deviceId)) {
        return { success: false, error: 'Invalid device ID format' };
      }
    }

    // Request package from API
    const response = await requestDownloadPackage(contentIds, format, accessToken, deviceId);

    if (response.error) {
      return { success: false, error: response.error.message };
    }

    if (!response.data) {
      return { success: false, error: 'No data received from server' };
    }

    const { manifest, signedManifest, files } = response.data;

    // Generate package based on format
    let blob: Blob;
    
    if (format === 'development') {
      blob = await generateDevelopmentPackage(manifest, files, accessToken, onProgress);
    } else {
      if (!signedManifest || !deviceId) {
        return { success: false, error: 'Signed manifest required for production mode' };
      }
      blob = await generateProductionPackage(
        manifest, 
        signedManifest, 
        files, 
        accessToken, 
        deviceId, 
        onProgress
      );
    }

    // Trigger download
    const filename = generatePackageFilename(manifest);
    downloadBlob(blob, filename);

    return { success: true };

  } catch (error) {
    console.error('Download error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Download failed' 
    };
  }
}
